import { ref, type Ref, computed } from 'vue';

import { sendPost } from '@/Utils/api';
import { rideTypes } from '@/constants/ride';
import { useAddress } from '@/use/address';
import type { CustomLocationType } from '@/models/address';
import { type Caller, plansBeforeSoftRegRideRegistrationSteps, RideRegistrationStepKey, rideRegistrationSteps } from '@/models/caller';
import type { LovedOne } from '@/models/lovedOne';
import { RideServiceType, type RideEstimate } from '@/models/ride';
import { planCurrency, visiblePlans } from '@/services/pricing/pricingViewModel';
import { isPlansBeforeSoftRegTest } from '@/services/operators/viewModel';
import { resetComparisonFramingState } from '@/services/comparison-framing/ComparisonFramingViewModel';

const { getAnchorBound, checkOutsideUSAddress } = useAddress();

const pickupAddress: Ref<CustomLocationType | null> = ref(null);
const dropoffAddress: Ref<CustomLocationType | null> = ref(null);
const rideType = ref(rideTypes[0].key);
const serviceType = ref(RideServiceType.Transportation);
const estimationRide: Ref<RideEstimate | null> = ref(null);
const newCustomer: Ref<Caller | null> = ref(null);
const associatedLovedOnes: Ref<LovedOne[]> = ref([]);
const isPaymentAdded = ref(false);
const isCompletedOrdered = ref(false);
const selectedPlan = ref('');
const totalRides: Ref<number | null> = ref(null);
const activeRegistrationStepKey: Ref<RideRegistrationStepKey | null> = ref(null);
const registrationEpoch = ref(0);

const selectedPlanMonthlyPrice = computed(() => {
  if (!selectedPlan.value) {
    return null;
  }

  const selectedPlanPricing = visiblePlans.value.find(
    ({ pricing }) => pricing.annual?.planKind === selectedPlan.value || pricing.monthly?.planKind === selectedPlan.value,
  );
  const isAnnualPlan = selectedPlan.value.includes('Annual');

  return (
    (isAnnualPlan
      ? selectedPlanPricing?.pricing?.annualPerMonthPrice?.[planCurrency.value]
      : selectedPlanPricing?.pricing?.monthly?.price[planCurrency.value]) ?? null
  );
});

const pickupAddressAnchorBound = computed(() => getAnchorBound(pickupAddress.value));
const dropoffAddressAnchorBound = computed(() => getAnchorBound(dropoffAddress.value));
const isTransportation = computed(() => serviceType.value === RideServiceType.Transportation);
const isSpecialRequestOrder = computed(() => serviceType.value === RideServiceType.SpecialRequest);
const isPickupOutsideUS = computed(() => checkOutsideUSAddress(pickupAddress.value));
const isDropoffOutsideUS = computed(() => checkOutsideUSAddress(dropoffAddress.value));
const isRideOutsideUS = computed(() => Boolean(isPickupOutsideUS.value || isDropoffOutsideUS.value));

export const getRideRegistrationSteps = computed(() => {
  const plansBeforeSoftReg = Boolean(isPlansBeforeSoftRegTest.value);
  const steps = plansBeforeSoftReg ? plansBeforeSoftRegRideRegistrationSteps : rideRegistrationSteps;
  return steps;
});

export const getResumeRideRegistrationSteps = computed(() => {
  const plansBeforeSoftReg = Boolean(isPlansBeforeSoftRegTest.value);
  const steps = plansBeforeSoftReg ? plansBeforeSoftRegRideRegistrationSteps : rideRegistrationSteps;
  return [
    { step: 0, title: 'Pull up Customer Info', key: RideRegistrationStepKey.PullupCustomerInfo },
    ...steps.map((item) => ({ step: item.step + 1, title: item.title, key: item.key })),
  ];
});

const resetRegistrationData = (isClearEstimations: boolean) => {
  pickupAddress.value = null;
  dropoffAddress.value = null;
  rideType.value = rideTypes[0].key;
  newCustomer.value = null;
  associatedLovedOnes.value = [];
  isPaymentAdded.value = false;
  isCompletedOrdered.value = false;
  selectedPlan.value = '';
  totalRides.value = null;
  activeRegistrationStepKey.value = null;
  registrationEpoch.value += 1;
  resetComparisonFramingState();

  if (isClearEstimations) {
    estimationRide.value = null;
  }
};

const getEstimation = ({
  apiName = 'getWebEstimate',
  phoneNumber = '',
  noteToDriver = '',
  operatorNote,
  typeOfCar,
  callerId,
  isFromLeadRegistration,
}: {
  apiName?: string;
  phoneNumber?: string;
  noteToDriver?: string;
  operatorNote?: string;
  typeOfCar?: string;
  callerId?: string;
  isFromLeadRegistration?: boolean;
} = {}) => {
  if (!pickupAddress.value) {
    return;
  }

  const endAddressPayload = dropoffAddress.value
    ? {
        endAddress: dropoffAddress.value.streetAddress,
        endCity: dropoffAddress.value.city,
        endState: dropoffAddress.value.state,
        endZip: dropoffAddress.value.zip,
        endName: dropoffAddress.value.name,
        endCoors: {
          lat: dropoffAddress.value.latitude,
          lng: dropoffAddress.value.longitude,
        },
      }
    : null;

  const payload = {
    serviceType: serviceType.value,
    startAddress: pickupAddress.value.streetAddress,
    startCity: pickupAddress.value.city,
    startState: pickupAddress.value.state,
    startZip: pickupAddress.value.zip,
    startName: pickupAddress.value.name,
    startCoors: {
      lat: pickupAddress.value.latitude,
      lng: pickupAddress.value.longitude,
    },
    ...(endAddressPayload || {}),
    ...(phoneNumber && { phoneNumber, riderPhone: phoneNumber }),
    ...(callerId && { callerId }),
    ...(noteToDriver && { noteToDriver }),
    ...(operatorNote && { operatorNote }),
    ...(typeOfCar ? { rideType: typeOfCar } : { isAllVendors: true }),
    ...(isFromLeadRegistration && { isFromLeadRegistration }),
  };

  return sendPost(apiName, payload);
};

export {
  pickupAddress,
  dropoffAddress,
  rideType,
  serviceType,
  estimationRide,
  newCustomer,
  totalRides,
  associatedLovedOnes,
  isPaymentAdded,
  isCompletedOrdered,
  pickupAddressAnchorBound,
  dropoffAddressAnchorBound,
  isTransportation,
  isSpecialRequestOrder,
  selectedPlanMonthlyPrice,
  selectedPlan,
  isRideOutsideUS,
  activeRegistrationStepKey,
  registrationEpoch,
  resetRegistrationData,
  getEstimation,
};
